import { __decorate } from "tslib";
import { Component } from "vue-property-decorator";
import BaseMixer from "@/router/views/BaseMixer";
import { DateFormatConstant } from "@/store/DateFormatConstants";
import ClientModule from "@/store/modules/client.module";
import { clientRiskColorIcon, clientRiskColorName, clientRiskLevelName } from "@/store/models/client/clientRisk";
/**
 * Display helpers shared by the ClientDetails page and its tab components.
 */
let ClientDetailsMixin = /** @class */ (() => {
    let ClientDetailsMixin = class ClientDetailsMixin extends BaseMixer {
        getInitials(name) {
            if (!name) {
                return "";
            }
            return name
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((part) => part.charAt(0).toUpperCase())
                .join("");
        }
        // The API always returns a URL. With no upload that URL is the shared user-default.svg.
        hasContactPhoto(url) {
            const value = (url || "").trim();
            return !!value && value.toLowerCase().indexOf("user-default.svg") === -1;
        }
        formatContactAddress(address) {
            if (!address) {
                return "";
            }
            return [address.addressLine1, address.addressLine2, address.city, address.state, address.country].filter((part) => !!part).join(", ");
        }
        formatUSAddress(address) {
            if (!address) {
                return "";
            }
            const lines = [];
            if (address.addressLine1) {
                lines.push(address.addressLine1);
            }
            if (address.addressLine2) {
                lines.push(address.addressLine2);
            }
            const cityStateZip = [address.city, [address.state, address.zip].filter((part) => !!part).join(" ")].filter((part) => !!part).join(", ");
            if (cityStateZip) {
                lines.push(cityStateZip);
            }
            if (address.country) {
                lines.push(address.country);
            }
            return lines.join("\n");
        }
        // Only absolute http(s) links are clickable; anything else (javascript:, relative text) renders as plain text
        safeWebLink(value) {
            const link = (value || "").trim();
            return /^https?:\/\/[^\s]+$/i.test(link) ? link : "";
        }
        formatClientDate(value) {
            if (!value) {
                return "";
            }
            const date = value instanceof Date ? value : new Date(value);
            if (isNaN(date.getTime())) {
                return "";
            }
            return this.dateFormat === DateFormatConstant.DateFormat.DateFormatMDY() ? this.dateFormattedString(date) : this.dateFormatted(date);
        }
        // Calendar day as local midnight; server dates ("2026-03-01T00:00:00") use their date part, so no timezone shift
        toCalendarDay(value) {
            if (!value) {
                return null;
            }
            if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                return new Date(Number(value.substring(0, 4)), Number(value.substring(5, 7)) - 1, Number(value.substring(8, 10)));
            }
            const date = value instanceof Date ? value : new Date(value);
            return isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate());
        }
        // True when the date falls on a day before the client's Start Date (same day allowed; no Start Date = no limit)
        isBeforeClientStartDate(value, startDate) {
            const day = this.toCalendarDay(value);
            const start = this.toCalendarDay(startDate);
            return !!day && !!start && day < start;
        }
        // Risk level name from PMORisks (ClientModule.GetRiskLevels must have been called)
        riskColorShortLabel(color) {
            return clientRiskLevelName(ClientModule.riskLevels, color);
        }
        riskColorIcon(color) {
            return clientRiskColorIcon(color);
        }
        // badge-risk-red / -orange / -green, or badge-risk-other for a level without a colour of its own
        riskColorClass(color) {
            return `badge-risk-${clientRiskColorName(color)}`;
        }
        insightText(value) {
            const text = (value || "")
                .replace(/<[^>]*>/g, " ")
                .replace(/&nbsp;/g, " ")
                .replace(/\s+/g, " ")
                .trim();
            return text || "No description recorded.";
        }
    };
    ClientDetailsMixin = __decorate([
        Component
    ], ClientDetailsMixin);
    return ClientDetailsMixin;
})();
export default ClientDetailsMixin;
